import Loader from '@/components/Loader';
import { RadioGroupItem } from '@communityshare/ui-shared';
import TagTypes from '@/constants/TagTypes';
import useTags from '@/hooks/useTags';
import useUserTags from '@/hooks/useUserTags';
import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useUser } from '@lore/auth';
import { useQueryMutation } from '@lore/query-connect';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

function GradeSelection(props) {
  const { titleText, grades, userGrades, optimistic, onSelectGrade } = props;
  return (
    <div className="bg-white p-2.5 rounded">
      <h4 className="mb-3 text-base font-semibold text-cs-gray-900">{titleText}</h4>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Grades">
        {grades.data.map((grade) => {
          const serverSelected = !!_.find(userGrades.data, (n) => n._tag.id === grade.id);
          const selected = grade.id in optimistic ? optimistic[grade.id] : serverSelected;
          return (
            <RadioGroupItem
              key={grade.id}
              label={grade.data.name}
              selected={selected}
              onChange={() => onSelectGrade(grade)}
            />
          );
        })}
      </div>
    </div>
  );
}

GradeSelection.propTypes = {
  titleText: PropTypes.string.isRequired,
  grades: PropTypes.object.isRequired,
  userGrades: PropTypes.object.isRequired,
  optimistic: PropTypes.object.isRequired,
  onSelectGrade: PropTypes.func.isRequired,
};

export default function EditableGrades(props) {
  const { showEditToggle = true } = props;
  const currentUser = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [optimistic, setOptimistic] = useState({});
  const grades = useTags(TagTypes.Grade);
  const userGrades = useUserTags(currentUser, TagTypes.Grade);

  const mutationCreate = useQueryMutation('tagNode.create');
  const mutationDestroy = useQueryMutation('tagNode.destroy2');

  if (!currentUser || grades.state === 'FETCHING' || userGrades.state === 'FETCHING') {
    return <Loader />;
  }

  function clearOptimistic(gradeId) {
    setOptimistic((prev) => { const next = { ...prev }; delete next[gradeId]; return next; });
  }

  function onSelectGrade(grade) {
    const node = _.find(userGrades.data, (n) => n._tag.id === grade.id);
    const isAdding = !node;
    setOptimistic((prev) => ({ ...prev, [grade.id]: isAdding }));

    if (isAdding) {
      mutationCreate.mutate(
        { userId: currentUser.id, tagId: grade.id },
        {
          onSuccess: (payload) => userGrades._add(payload),
          onError: () => clearOptimistic(grade.id),
        }
      );
    } else {
      mutationDestroy.mutate(node._tagNode, {
        onSuccess: (payload) => userGrades._remove(payload),
        onError: () => clearOptimistic(grade.id),
      });
    }
  }

  const titleText = 'What grades do you teach?';

  if (!showEditToggle) {
    return (
      <GradeSelection
        titleText={titleText}
        grades={grades}
        userGrades={userGrades}
        optimistic={optimistic}
        onSelectGrade={onSelectGrade}
      />
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center space-x-3">
        <h4 className="text-base font-semibold text-cs-gray-900">{titleText}</h4>
        <button
          type="button"
          className="text-cs-blue-500 hover:opacity-60"
          onClick={() => setIsEditing(!isEditing)}
          aria-label={isEditing ? 'Close edit grades' : 'Edit grades'}
        >
          {isEditing ? <XMarkIcon className="size-5" /> : <PencilSquareIcon className="size-5" />}
        </button>
      </div>
      {isEditing ? (
        <GradeSelection
          titleText={titleText}
          grades={grades}
          userGrades={userGrades}
          optimistic={optimistic}
          onSelectGrade={onSelectGrade}
        />
      ) : (
        <div className="space-y-1 text-base text-cs-gray-500">
          {userGrades.data.length > 0 ? (
            userGrades.data.map((ug) => <p key={ug.id}>{ug.data.name}</p>)
          ) : (
            <p className="italic text-cs-gray-400">No grades selected.</p>
          )}
        </div>
      )}
    </div>
  );
}

EditableGrades.propTypes = {
  showEditToggle: PropTypes.bool,
};
